import Grid from '@mui/material/Grid';
import { useAppSelector } from '../../app/hooks';
import { useSummaryQuery, usePayoutQuery } from '../../services/api';
import { CHMAP, CHCOLOR } from '../../constants/channels';
import { inr, inrShort, pct, fmtPct, deltaPct, dfmt } from '../../utils/format';
import { KpiGrid } from '../../components/common/KpiCard';
import DataCard from '../../components/common/DataCard';
import QueryState from '../../components/common/QueryState';
import { Shimmer, ShimmerText, KpiSkeleton, ChartSkeleton } from '../../components/common/Shimmer';
import { InsightRow } from '../../components/common/Bits';
import HBarChart from '../../components/charts/HBarChart';
import LineChart from '../../components/charts/LineChart';

/** Same shape as the loaded dashboard: 6 KPIs, insight strip, then four panels. */
function ChannelDashboardSkeleton() {
  return (
    <>
      <KpiSkeleton columns={6} />
      <Grid container spacing={1.75} sx={{ mb: 2.25 }}>
        {[0, 1, 2].map((i) => (
          <Grid key={i} size={{ xs: 12, md: 4 }}><Shimmer height={62} radius={12} /></Grid>
        ))}
      </Grid>
      <Grid container spacing={2}>
        <Grid size={12}><ChartSkeleton height={250} bars={16} /></Grid>
        <Grid size={{ xs: 12, lg: 6 }}><ChartSkeleton height={190} bars={7} /></Grid>
        <Grid size={{ xs: 12, lg: 6 }}><ChartSkeleton height={190} bars={7} /></Grid>
        <Grid size={12}><ChartSkeleton height={190} bars={7} /></Grid>
      </Grid>
    </>
  );
}

export default function ChannelDashboard({ channelId }) {
  const range = useAppSelector((s) => s.ui.range);
  const summaryQuery = useSummaryQuery({ range, channel: channelId });
  const payoutQuery = usePayoutQuery({ range, channel: channelId });

  const c = CHMAP[channelId];
  const col = CHCOLOR[channelId];
  const isD2C = channelId === 'd2c';

  return (
    <QueryState query={summaryQuery} skeleton={<ChannelDashboardSkeleton />}>
      {(data) => {
        const a = data.current;
        const prev = data.previous;
        const inf = payoutQuery.data?.totals?.payout ?? 0;
        const pending = payoutQuery.isLoading;

        const trend = [{ name: `${c.name} revenue`, color: col, data: data.byDate.map((r) => ({ d: r.date, v: r.v })) }];
        const best = [...data.byDate].sort((x, y) => y.v - x.v)[0] || { date: '', v: 0 };
        const topTest = data.topTests[0] || { k: '—', v: 0 };

        return (
          <>
            <KpiGrid
              columns={6}
              items={[
                { label: 'Revenue', value: inrShort(a.rev), sub: `${a.n} txns`, accent: col, delta: deltaPct(a.rev, prev.rev) },
                { label: 'Avg Ticket', value: inr(a.avg), sub: 'Paid per txn', accent: 'var(--s3)', delta: deltaPct(a.avg, prev.avg) },
                { label: 'Discount', value: inrShort(a.disc), sub: `${fmtPct(a.discPct)} of MRP`, accent: 'var(--s2)' },
                { label: 'Realization', value: fmtPct(a.real), sub: `of ${inrShort(a.mrp)} MRP`, accent: 'var(--s4)' },
                { label: 'Margin', value: inrShort(a.margin), sub: `${fmtPct(a.marginPct)} of rev`, accent: 'var(--s5)' },
                {
                  label: 'Influencer Payout',
                  value: pending ? <ShimmerText chars={6} height={22} /> : inrShort(inf),
                  sub: pending ? 'Still adding up' : `${fmtPct(pct(inf, a.rev))} · net ${inrShort(a.margin - inf)}`,
                  accent: 'var(--gold)',
                },
              ]}
            />

            <InsightRow
              items={[
                { icon: '📈', title: `Best day: ${best.date ? dfmt(best.date) : '—'}`, text: `${inrShort(best.v)} · avg ${inrShort(a.rev / Math.max(1, range))}/day.` },
                { icon: '🧪', title: `Top test: ${topTest.k}`, text: `${inrShort(topTest.v)} · ${fmtPct(pct(topTest.v, a.rev))} of revenue.` },
                {
                  icon: a.real < 60 ? '⚠️' : '✅',
                  title: `${a.real < 60 ? 'Thin' : 'Healthy'} realization: ${fmtPct(a.real)}`,
                  text: `₹100 MRP → ₹${a.real.toFixed(0)} collected.`,
                },
              ]}
            />

            <Grid container spacing={2}>
              <Grid size={12}>
                <DataCard title={`${c.full} — daily revenue`} subtitle="Collections per day · hover for detail">
                  <LineChart series={trend} height={250} />
                </DataCard>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <DataCard stretch title="Top tests by revenue" subtitle="Highest earners">
                  <HBarChart items={data.topTests.slice(0, 7)} color={col} />
                </DataCard>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <DataCard
                  stretch
                  title={`Top ${isD2C ? 'patients' : 'accounts'} by revenue`}
                  subtitle={isD2C ? 'Walk-in patients' : 'Partner / contract accounts'}
                >
                  <HBarChart items={data.topParties.slice(0, 7)} color="var(--s1)" />
                </DataCard>
              </Grid>
              <Grid size={12}>
                <DataCard title="Test volume (count)" subtitle="Most ordered tests">
                  <HBarChart items={data.volTests.slice(0, 7)} color="var(--s3)" fmt={(v) => v} />
                </DataCard>
              </Grid>
            </Grid>
          </>
        );
      }}
    </QueryState>
  );
}
